<script setup>
import { useReport } from '@/composables/useReport'

// pages/reports/index.vue — Module 20 (§28). Pilih jenis laporan + filter +
// tombol export (PDF/Excel), generate on-demand, tidak pernah disimpan ke
// database.
definePage({
  meta: {
    layout: 'default',
    action: 'export',
    subject: 'Report',
  },
})

const { report, loading, exporting, generate, exportFile, REPORT_TYPES } = useReport()

const type = ref('audit')
const filters = ref({ from: null, to: null, unit_id: null })

const load = () => generate(type.value, filters.value)

onMounted(load)

watch(type, () => {
  filters.value.unit_id = type.value === 'audit' ? null : filters.value.unit_id
  load()
})

const headings = computed(() => report.value?.rows?.length ? Object.keys(report.value.rows[0]) : [])
</script>

<template>
  <div>
    <h4 class="text-h4 font-weight-bold mb-1">
      Laporan
    </h4>
    <p class="text-body-2 text-medium-emphasis mb-4">
      6 laporan wajib — generate langsung, ekspor PDF/Excel kapan saja (§28)
    </p>

    <VCard class="mb-4">
      <VCardText class="pa-6">
        <VRow class="mb-2">
          <VCol
            cols="12"
            md="4"
          >
            <VSelect
              v-model="type"
              :items="REPORT_TYPES"
              item-title="title"
              item-value="value"
              label="Jenis Laporan"
            />
          </VCol>
        </VRow>

        <ReportFilterBar
          v-model="filters"
          :report-type="type"
          @apply="load"
        />

        <div class="d-flex gap-3 mt-4">
          <VBtn
            :loading="exporting"
            variant="tonal"
            prepend-icon="ri-file-pdf-2-line"
            @click="exportFile(type, 'pdf', filters)"
          >
            Export PDF
          </VBtn>
          <VBtn
            :loading="exporting"
            variant="tonal"
            prepend-icon="ri-file-excel-2-line"
            @click="exportFile(type, 'excel', filters)"
          >
            Export Excel
          </VBtn>
        </div>
      </VCardText>
    </VCard>

    <VCard>
      <VCardTitle class="pa-6 pb-0">
        {{ report?.title ?? 'Pratinjau' }}
      </VCardTitle>
      <VCardText class="pa-6">
        <VProgressLinear
          v-if="loading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <div
          v-else-if="report?.rows?.length"
          style="overflow-x: auto;"
        >
          <table class="report-preview-table">
            <thead>
              <tr>
                <th
                  v-for="h in headings"
                  :key="h"
                >
                  {{ h }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in report.rows"
                :key="idx"
              >
                <td
                  v-for="h in headings"
                  :key="h"
                >
                  {{ row[h] }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p
          v-else
          class="text-medium-emphasis mb-0"
        >
          Tidak ada data untuk filter ini.
        </p>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.report-preview-table {
  width: 100%;
  border-collapse: collapse;
}

.report-preview-table th,
.report-preview-table td {
  padding: 8px 12px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  text-align: left;
  white-space: nowrap;
}
</style>
